import { useTranslation } from 'react-i18next';

import { Button, IconButton } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { MAX_IMAGE_CLIP_DURATION, MIN_IMAGE_CLIP_DURATION } from '@/helpers/CustomVideoThemeHelper';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon, DeleteIcon, PhotoFilmIcon } from '@/resources';
import { SelectedThemeClip } from '@/types/CustomVideoThemeTypes';

type ClipSectionProps = {
  addButtonText: string;
  clips: SelectedThemeClip[];
  isImageAsset: (assetType: string) => boolean;
  onDurationChange: (assetId: number, value: string) => void;
  onRemoveClip: (assetId: number) => void;
  onAddClip: () => void;
};

export const ClipSection = ({
  addButtonText,
  clips,
  isImageAsset,
  onDurationChange,
  onRemoveClip,
  onAddClip
}: ClipSectionProps) => {
  const [t] = useTranslation('common');
  return (
    <div className='w-full flex-1 lg:mx-2'>
      <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-primary-blue p-4 lg:ml-1'>
        <div className='my-3 w-full'>
          {clips.length === 0 && (
            <p className='text-center text-sm text-secondary-gray'>{t('noClipsSelectedYet')}</p>
          )}
          {clips.map((clip) => (
            <div
              key={clip.assetId}
              className='mb-2 flex items-center justify-between rounded-md border border-secondary-blue-gray px-3 py-2'>
              <p className='mr-2 min-w-0 max-w-[150px] truncate text-sm font-semibold text-primary-blue md:max-w-[180px] xl:max-w-[200px] 2xl:max-w-[250px] 3xl:max-w-[300px] 4xl:max-w-[400px]'>
                {clip.assetName}
              </p>
              {isImageAsset(clip.assetType) ? (
                <div className='flex max-w-32 items-center justify-between gap-4'>
                  <div className='flex flex-row items-center gap-1'>
                    <span className='text-sm font-semibold text-secondary-gray'>
                      {clip.duration}s
                    </span>
                    <div className='flex flex-col'>
                      <button
                        type='button'
                        className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
                        onClick={() =>
                          onDurationChange(
                            clip.assetId,
                            String(Math.min(clip.duration + 1, MAX_IMAGE_CLIP_DURATION))
                          )
                        }>
                        <ArrowUpHeavyIcon className='size-3 text-primary-blue' />
                      </button>
                      <button
                        type='button'
                        className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
                        onClick={() =>
                          onDurationChange(
                            clip.assetId,
                            String(Math.max(clip.duration - 1, MIN_IMAGE_CLIP_DURATION))
                          )
                        }>
                        <ArrowDownHeavyIcon className='size-3 text-primary-blue' />
                      </button>
                    </div>
                  </div>

                  <IconButton
                    variant={IconButtonVariant.SECONDARY}
                    handleOnClick={() => onRemoveClip(clip.assetId)}>
                    <DeleteIcon className='text-primary-blue' />
                  </IconButton>
                </div>
              ) : (
                <div className='flex max-w-28 items-center justify-between gap-4'>
                  <span className='pr-4 text-sm font-semibold text-secondary-gray'>
                    {clip.duration}s
                  </span>
                  <IconButton
                    variant={IconButtonVariant.SECONDARY}
                    handleOnClick={() => onRemoveClip(clip.assetId)}>
                    <DeleteIcon className='text-primary-blue' />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </div>
        <Button type='button' className='my-2 gap-2' handleOnClick={onAddClip}>
          <PhotoFilmIcon className='size-4' />
          {addButtonText}
        </Button>
      </div>
    </div>
  );
};
